const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

// Motion: every panel in the theme opens and closes through these helpers.
const MOTION = { fast: 160, base: 280, slow: 420, easing: 'cubic-bezier(.2,.8,.2,1)' };

function showPanel(element) {
  clearTimeout(element.bxTimer);
  element.hidden = false;
  void element.offsetHeight;
  element.classList.add('is-open');
}

function hidePanel(element, duration = MOTION.base) {
  element.classList.remove('is-open');
  clearTimeout(element.bxTimer);
  element.bxTimer = setTimeout(() => {
    element.hidden = true;
  }, reduceMotion.matches ? 0 : duration);
}

function animateDetails(details, open) {
  if (details.bxAnimation) details.bxAnimation.cancel();
  const summary = details.querySelector('summary');
  const start = details.offsetHeight;
  if (open) details.open = true;
  details.classList.toggle('is-open', open);
  const end = open ? details.scrollHeight : summary.offsetHeight + (details.offsetHeight - details.clientHeight);
  if (reduceMotion.matches) {
    details.open = open;
    return;
  }
  details.style.overflow = 'hidden';
  details.bxAnimation = details.animate({ height: [`${start}px`, `${end}px`] }, { duration: MOTION.base, easing: MOTION.easing });
  details.bxAnimation.onfinish = () => {
    details.open = open;
    details.style.overflow = '';
    details.bxAnimation = null;
  };
}

document.addEventListener('click', (event) => {
  const summary = event.target.closest('.bx-acc > summary, .bx-footer__col > summary');
  if (!summary) return;
  event.preventDefault();
  const details = summary.parentElement;
  animateDetails(details, !details.classList.contains('is-open'));
});

function closeLang(lang) {
  lang.classList.add('is-closing');
  setTimeout(() => {
    lang.open = false;
    lang.classList.remove('is-closing');
  }, reduceMotion.matches ? 0 : MOTION.fast);
}

class BxTicker extends HTMLElement {
  connectedCallback() {
    this.items = [...this.querySelectorAll('.bx-topbar__usp')];
    if (this.items.length < 2 || reduceMotion.matches) return;
    this.index = 0;
    this.timer = setInterval(() => this.next(), 4000);
  }

  disconnectedCallback() {
    clearInterval(this.timer);
  }

  next() {
    this.items[this.index].classList.remove('is-on');
    this.index = (this.index + 1) % this.items.length;
    this.items[this.index].classList.add('is-on');
  }
}
customElements.define('bx-ticker', BxTicker);

class BxHeader extends HTMLElement {
  connectedCallback() {
    this.mega = this.querySelector('[data-bx-mega]');
    this.backdrop = this.querySelector('[data-bx-mega-backdrop]');
    this.toggle = this.querySelector('[data-bx-mega-toggle]');
    this.drawer = this.querySelector('[data-bx-drawer]');
    this.burger = this.querySelector('[data-bx-drawer-open]');

    if (this.toggle && this.mega) {
      const canHover = window.matchMedia('(hover: hover) and (pointer: fine)');
      this.toggle.addEventListener('click', (event) => {
        clearTimeout(this.megaTimer);
        // A mouse has already opened it on hover, so a click never closes it by surprise.
        const isOpen = this.mega.classList.contains('is-open');
        this.setMega(canHover.matches && event.detail > 0 ? true : !isOpen);
      });
      const hoverAfter = (open, delay) => {
        if (!canHover.matches) return;
        clearTimeout(this.megaTimer);
        this.megaTimer = setTimeout(() => {
          if (open !== this.mega.classList.contains('is-open')) this.setMega(open);
        }, delay);
      };
      [this.toggle, this.mega].forEach((element) => {
        element.addEventListener('mouseenter', () => hoverAfter(true, 120));
        element.addEventListener('mouseleave', () => hoverAfter(false, 220));
      });
      this.backdrop.addEventListener('click', () => this.setMega(false));
      this.querySelectorAll('[data-bx-mega-cat]').forEach((cat) => {
        const show = () => this.showCategory(cat.dataset.bxMegaCat);
        cat.addEventListener('mouseenter', show);
        cat.addEventListener('focus', show);
      });
    }

    this.search = this.querySelector('[data-bx-search]');
    this.searchToggle = this.querySelector('[data-bx-search-toggle]');
    this.searchInput = this.querySelector('[data-bx-search-input]');
    this.searchResults = this.querySelector('[data-bx-search-results]');
    this.searchToggle?.addEventListener('click', () => this.setSearch(this.search.hidden || !this.search.classList.contains('is-open')));
    this.searchInput?.addEventListener('input', () => {
      clearTimeout(this.searchTimer);
      this.searchTimer = setTimeout(() => this.suggest(), 220);
    });
    this.backdrop?.addEventListener('click', () => this.setSearch(false));

    this.burger?.addEventListener('click', () => this.setDrawer(true));
    this.querySelector('[data-bx-drawer-close]')?.addEventListener('click', () => this.setDrawer(false));
    this.querySelector('[data-bx-drawer-back]')?.addEventListener('click', () => this.showDrawerView(null));
    this.querySelectorAll('[data-bx-drawer-sub]').forEach((tile) => {
      tile.addEventListener('click', () => this.showDrawerView(tile.dataset.bxDrawerSub));
    });

    this.onKey = (event) => {
      if (event.key !== 'Escape') return;
      if (this.drawer && !this.drawer.hidden) this.setDrawer(false);
      else if (this.search && !this.search.hidden) {
        this.setSearch(false);
        this.searchToggle.focus();
      } else if (this.mega && !this.mega.hidden) {
        this.setMega(false);
        this.toggle.focus();
      }
    };
    document.addEventListener('keydown', this.onKey);
  }

  disconnectedCallback() {
    document.removeEventListener('keydown', this.onKey);
    document.documentElement.classList.remove('bx-lock');
  }

  setMega(open) {
    if (open && this.search && !this.search.hidden) this.setSearch(false, true);
    if (open) {
      showPanel(this.mega);
      showPanel(this.backdrop);
    } else {
      hidePanel(this.mega);
      if (!this.search || !this.search.classList.contains('is-open')) hidePanel(this.backdrop);
    }
    this.toggle.setAttribute('aria-expanded', String(open));
  }

  setSearch(open, keepBackdrop = false) {
    if (open && this.mega && this.mega.classList.contains('is-open')) this.setMega(false);
    if (open) {
      showPanel(this.search);
      showPanel(this.backdrop);
      this.searchInput.focus();
    } else {
      hidePanel(this.search);
      if (!keepBackdrop && !(this.mega && this.mega.classList.contains('is-open'))) hidePanel(this.backdrop);
    }
    this.searchToggle.setAttribute('aria-expanded', String(open));
  }

  async suggest() {
    const terms = this.searchInput.value.trim();
    if (terms.length < 2) {
      this.searchResults.innerHTML = '';
      return;
    }
    const params = new URLSearchParams({ q: terms, section_id: 'predictive-search', 'resources[type]': 'product', 'resources[limit]': '6' });
    const html = await fetch(`${window.Shopify?.routes?.root || '/'}search/suggest?${params}`).then((response) => response.text());
    const section = new DOMParser().parseFromString(html, 'text/html').querySelector('.shopify-section');
    if (this.searchInput.value.trim() === terms && section) this.searchResults.innerHTML = section.innerHTML;
  }

  showCategory(id) {
    this.querySelectorAll('[data-bx-mega-cat]').forEach((cat) => {
      cat.classList.toggle('is-on', cat.dataset.bxMegaCat === id);
    });
    this.querySelectorAll('[data-bx-mega-panel]').forEach((panel) => {
      panel.hidden = panel.dataset.bxMegaPanel !== id;
    });
  }

  setDrawer(open) {
    if (open) showPanel(this.drawer);
    else hidePanel(this.drawer, MOTION.slow);
    this.burger.setAttribute('aria-expanded', String(open));
    document.documentElement.classList.toggle('bx-lock', open);
    if (open) {
      this.showDrawerView(null);
      this.querySelector('[data-bx-drawer-close]').focus();
    } else {
      this.burger.focus();
    }
  }

  showDrawerView(id) {
    this.drawer.classList.toggle('is-back', !id);
    this.querySelector('[data-bx-drawer-root]').hidden = Boolean(id);
    this.querySelector('[data-bx-drawer-back]').hidden = !id;
    this.querySelector('[data-bx-drawer-logo]').hidden = Boolean(id);
    this.querySelectorAll('[data-bx-drawer-view]').forEach((view) => {
      view.hidden = view.dataset.bxDrawerView !== id;
    });
    this.drawer.scrollTop = 0;
  }
}
customElements.define('bx-header', BxHeader);

class BxHero extends HTMLElement {
  connectedCallback() {
    this.video = this.querySelector('video');
    this.pause = this.querySelector('[data-bx-hero-pause]');
    if (!this.video || !this.pause) return;
    if (reduceMotion.matches) this.setPlaying(false);
    this.pause.addEventListener('click', () => this.setPlaying(this.video.paused));
  }

  setPlaying(playing) {
    if (playing) this.video.play();
    else this.video.pause();
    this.pause.setAttribute('aria-pressed', String(!playing));
  }
}
customElements.define('bx-hero', BxHero);

const shopRoot = window.Shopify?.routes?.root || '/';

class BxCollection extends HTMLElement {
  connectedCallback() {
    this.form = this.querySelector('[data-bx-filter-form]');
    this.grid = this.querySelector('[data-bx-grid]');
    this.more = this.querySelector('[data-bx-more]');
    this.querySelector('[data-bx-filters-open]')?.addEventListener('click', () => this.setFilters(true));
    this.querySelector('[data-bx-filters-close]')?.addEventListener('click', () => this.setFilters(false));
    this.form?.addEventListener('change', (event) => {
      if (window.matchMedia('(max-width: 1023px)').matches) return;
      if (event.target.type === 'number') return;
      this.submit();
    });
    this.form?.addEventListener('submit', (event) => {
      event.preventDefault();
      this.submit();
    });
    this.querySelector('[data-bx-sort]')?.addEventListener('change', (event) => {
      this.querySelector('[data-bx-sort-field]').value = event.target.value;
      this.submit();
    });
    this.more?.addEventListener('click', (event) => this.loadMore(event));
  }

  setFilters(open) {
    this.form.classList.toggle('is-open', open);
    document.documentElement.classList.toggle('bx-lock', open);
  }

  submit() {
    const params = new URLSearchParams(new FormData(this.form));
    [...params.entries()].filter(([, value]) => value === '').forEach(([key]) => params.delete(key));
    this.classList.add('is-loading');
    window.location.search = params.toString();
  }

  async loadMore(event) {
    event.preventDefault();
    const url = this.more.href;
    this.more.setAttribute('aria-busy', 'true');
    try {
      const html = await fetch(url).then((response) => response.text());
      const page = new DOMParser().parseFromString(html, 'text/html');
      page.querySelectorAll('[data-bx-grid] > .bx-card').forEach((card) => this.grid.append(document.adoptNode(card)));
      const next = page.querySelector('[data-bx-more]');
      const pager = this.querySelector('[data-bx-pager]');
      const nextPager = page.querySelector('[data-bx-pager]');
      this.querySelector('[data-bx-pager-count]').textContent = nextPager.querySelector('[data-bx-pager-count]').textContent;
      this.querySelector('[data-bx-pager-bar]').style.width = nextPager.querySelector('[data-bx-pager-bar]').style.width;
      pager.querySelector('.bx-pages').replaceWith(document.adoptNode(nextPager.querySelector('.bx-pages')));
      history.replaceState(null, '', url);
      if (next) this.more.href = next.href;
      else this.more.remove();
    } catch {
      window.location.href = url;
    }
    this.more.removeAttribute('aria-busy');
  }
}
customElements.define('bx-collection', BxCollection);

// House-style dropdown on top of a native select, which keeps the value and fires change.
class BxSelect extends HTMLElement {
  connectedCallback() {
    this.select = this.querySelector('select');
    this.button = this.querySelector('[data-bx-select-button]');
    this.label = this.querySelector('[data-bx-select-label]');
    this.list = this.querySelector('[data-bx-select-list]');
    this.select.hidden = true;
    [...this.select.options].forEach((option, index) => {
      const item = document.createElement('li');
      item.setAttribute('role', 'option');
      item.tabIndex = -1;
      item.dataset.index = index;
      item.textContent = option.textContent;
      this.list.append(item);
    });
    this.items = [...this.list.children];
    this.mark();
    this.button.addEventListener('click', () => this.toggle(this.button.getAttribute('aria-expanded') !== 'true'));
    this.button.addEventListener('keydown', (event) => {
      if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
        event.preventDefault();
        this.toggle(true);
      }
    });
    this.list.addEventListener('click', (event) => {
      const item = event.target.closest('[role="option"]');
      if (item) this.choose(Number(item.dataset.index));
    });
    this.list.addEventListener('keydown', (event) => this.onKey(event));
    document.addEventListener('pointerdown', (event) => {
      if (!this.contains(event.target)) this.toggle(false);
    });
  }

  mark() {
    const current = this.select.selectedIndex;
    this.label.textContent = this.select.options[current].textContent;
    this.items.forEach((item, index) => item.setAttribute('aria-selected', index === current));
  }

  toggle(open) {
    if (open === (this.button.getAttribute('aria-expanded') === 'true')) return;
    this.button.setAttribute('aria-expanded', open);
    if (open) {
      showPanel(this.list);
      this.items[this.select.selectedIndex].focus();
    } else {
      hidePanel(this.list, MOTION.fast);
    }
  }

  choose(index) {
    const changed = index !== this.select.selectedIndex;
    this.select.selectedIndex = index;
    this.mark();
    this.toggle(false);
    this.button.focus();
    if (changed) this.select.dispatchEvent(new Event('change', { bubbles: true }));
  }

  onKey(event) {
    const index = this.items.indexOf(document.activeElement);
    const last = this.items.length - 1;
    const target = { ArrowDown: Math.min(index + 1, last), ArrowUp: Math.max(index - 1, 0), Home: 0, End: last }[event.key];
    if (target !== undefined) {
      event.preventDefault();
      this.items[target].focus();
    } else if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      this.choose(index);
    } else if (event.key === 'Escape') {
      this.toggle(false);
      this.button.focus();
    } else if (event.key === 'Tab') {
      this.toggle(false);
    }
  }
}
customElements.define('bx-select', BxSelect);

class BxProduct extends HTMLElement {
  connectedCallback() {
    this.variants = JSON.parse(this.querySelector('[data-bx-variants]').textContent);
    this.options = [...this.querySelectorAll('[data-bx-option]')];
    this.form = this.querySelector('[data-bx-form]');
    this.options.forEach((input) => input.addEventListener('change', () => this.update()));
    this.querySelectorAll('[data-bx-thumb]').forEach((thumb) => {
      thumb.addEventListener('click', () => this.showMedia(thumb));
    });
    this.querySelectorAll('[data-bx-qty]').forEach((button) => {
      button.addEventListener('click', () => {
        const input = this.querySelector('[name="quantity"]');
        input.value = Math.max(1, Number(input.value) + Number(button.dataset.bxQty));
      });
    });
    this.form?.addEventListener('submit', (event) => this.add(event));

    // Mobile buy bar appears once the main button has scrolled out of view.
    const bar = document.querySelector('[data-bx-buybar]');
    const main = this.querySelector('[data-bx-add]');
    if (bar && main) {
      let queued = false;
      const sync = () => {
        queued = false;
        bar.classList.toggle('is-open', main.getBoundingClientRect().bottom < 0);
      };
      window.addEventListener('scroll', () => {
        if (queued) return;
        queued = true;
        requestAnimationFrame(sync);
      }, { passive: true });
    }
  }

  showMedia(thumb) {
    const id = thumb.dataset.bxThumb;
    this.querySelectorAll('[data-bx-media]').forEach((item) => {
      item.hidden = item.dataset.bxMedia !== id;
      if (item.hidden) item.querySelector('video')?.pause();
    });
    const thumbs = [...this.querySelectorAll('[data-bx-thumb]')];
    thumbs.forEach((item) => item.classList.toggle('is-on', item === thumb));
    this.querySelector('[data-bx-media-index]').textContent = thumbs.indexOf(thumb) + 1;
  }

  update() {
    const chosen = this.options.filter((input) => input.checked).map((input) => input.value);
    const variant = this.variants.find((item) => item.options.every((value, index) => value === chosen[index]));
    if (!variant) return;
    this.querySelector('[data-bx-variant]').value = variant.id;
    const prices = JSON.parse(this.querySelector('[data-bx-prices]').textContent)[variant.id];
    this.querySelector('[data-bx-price]').textContent = prices.price;
    const compare = this.querySelector('[data-bx-compare]');
    compare.hidden = !(variant.compare_at_price > variant.price);
    if (!compare.hidden) compare.textContent = prices.compare;
    this.querySelector('[data-bx-stock-label]').textContent = prices.stock;
    this.querySelector('[data-bx-stock]').classList.toggle('is-low', prices.low);
    const bar = document.querySelector('[data-bx-buybar]');
    if (bar) {
      bar.querySelector('[data-bx-buybar-price]').textContent = prices.price;
      bar.querySelector('[data-bx-buybar-add]').disabled = !variant.available;
    }
    this.querySelector('[data-bx-instock]').hidden = !variant.available;
    this.querySelector('[data-bx-soldout]').hidden = variant.available;
    const body = this.querySelector('[data-bx-notify-body]');
    if (body) body.value = `${body.value.split(' · ')[0]} · ${variant.title}`;
    const url = new URL(window.location.href);
    url.searchParams.set('variant', variant.id);
    history.replaceState(null, '', url);
  }

  async add(event) {
    event.preventDefault();
    const button = this.querySelector('[data-bx-add]');
    const error = this.querySelector('[data-bx-error]');
    error.hidden = true;
    button.setAttribute('aria-busy', 'true');
    const body = new FormData(this.form);
    body.append('sections', 'cart-drawer');
    const response = await fetch(`${shopRoot}cart/add.js`, { method: 'POST', headers: { Accept: 'application/json' }, body });
    const data = await response.json();
    button.removeAttribute('aria-busy');
    if (!response.ok) {
      error.textContent = data.description || data.message;
      error.hidden = false;
      return;
    }
    document.querySelector('bx-cart-drawer')?.render(data.sections['cart-drawer'], true);
  }
}
customElements.define('bx-product', BxProduct);

class BxReadMore extends HTMLElement {
  connectedCallback() {
    this.body = this.querySelector('[data-bx-readmore-body]');
    this.toggle = this.querySelector('[data-bx-readmore-toggle]');
    this.closedHeight = 300;
    // Short texts stay as they are: only cut off when it saves real reading.
    if (this.body.scrollHeight <= this.closedHeight + 120) return;
    this.classList.add('is-clamped');
    this.toggle.hidden = false;
    this.toggle.addEventListener('click', () => this.set(!this.classList.contains('is-open')));
  }

  set(open) {
    const start = this.body.offsetHeight;
    this.classList.toggle('is-open', open);
    this.toggle.setAttribute('aria-expanded', String(open));
    this.toggle.textContent = open ? this.toggle.dataset.less : this.toggle.dataset.more;
    const end = open ? this.body.scrollHeight : this.closedHeight;
    if (!open && !reduceMotion.matches) this.scrollIntoView({ block: 'nearest' });
    if (reduceMotion.matches) return;
    this.body.animate({ height: [`${start}px`, `${end}px`] }, { duration: MOTION.slow, easing: MOTION.easing });
  }
}
customElements.define('bx-readmore', BxReadMore);

class BxCartDrawer extends HTMLElement {
  connectedCallback() {
    document.addEventListener('click', this.onClick = (event) => this.handleClick(event));
    document.addEventListener('keydown', this.onKey = (event) => {
      if (event.key === 'Escape' && !this.hidden) this.close();
    });
  }

  disconnectedCallback() {
    document.removeEventListener('click', this.onClick);
    document.removeEventListener('keydown', this.onKey);
  }

  handleClick(event) {
    const cartLink = event.target.closest('.bx-cartbtn');
    if (cartLink && !document.querySelector('[data-bx-cart-page]')) {
      event.preventDefault();
      this.opener = cartLink;
      this.open();
      return;
    }
    if (event.target.closest('[data-bx-cart-close]')) {
      this.close();
      return;
    }
    const line = event.target.closest('[data-bx-line]');
    if (line) this.change(line.dataset.bxLine, line.dataset.bxLineQty);
  }

  open() {
    showPanel(this);
    document.documentElement.classList.add('bx-lock');
    this.querySelector('[data-bx-cart-close].bx-iconbtn')?.focus();
  }

  close() {
    hidePanel(this, MOTION.slow);
    document.documentElement.classList.remove('bx-lock');
    this.opener?.focus();
  }

  async change(line, quantity) {
    const response = await fetch(`${shopRoot}cart/change.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ line: Number(line), quantity: Number(quantity), sections: 'cart-drawer' })
    });
    const data = await response.json();
    if (document.querySelector('[data-bx-cart-page]')) {
      window.location.reload();
      return;
    }
    this.render(data.sections['cart-drawer'], !this.hidden);
  }

  render(html, open) {
    const next = new DOMParser().parseFromString(html, 'text/html').querySelector('bx-cart-drawer');
    this.querySelector('.bx-cartdrawer__body').innerHTML = next.querySelector('.bx-cartdrawer__body').innerHTML;
    document.querySelectorAll('[data-bx-cart-count]').forEach((count) => {
      count.textContent = next.dataset.count;
      count.hidden = Number(next.dataset.count) === 0;
    });
    if (open) this.open();
  }
}
customElements.define('bx-cart-drawer', BxCartDrawer);

document.addEventListener('click', (event) => {
  const langSummary = event.target.closest('[data-bx-lang] > summary');
  if (langSummary && langSummary.parentElement.open) {
    event.preventDefault();
    closeLang(langSummary.parentElement);
  }
  document.querySelectorAll('[data-bx-lang][open]').forEach((lang) => {
    if (!lang.contains(event.target)) closeLang(lang);
  });
});

// Older product texts contain emoji. Hide them on display only; the store data stays untouched.
document.querySelectorAll('.bx-rte--product, .bx-rows, .bx-specline').forEach((root) => {
  const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
  const emoji = /[\p{Extended_Pictographic}\u{FE0F}\u{200D}\u{20E3}\u{1F3FB}-\u{1F3FF}]+\s*/gu;
  while (walker.nextNode()) {
    const node = walker.currentNode;
    if (emoji.test(node.nodeValue)) node.nodeValue = node.nodeValue.replace(emoji, '');
    emoji.lastIndex = 0;
  }
});

// Sections fade in once while scrolling. Skipped in the theme editor and for reduced motion.
if ('IntersectionObserver' in window && !reduceMotion.matches && !window.Shopify?.designMode) {
  const reveal = new IntersectionObserver((entries) => {
    entries.forEach((entry) => {
      if (!entry.isIntersecting) return;
      entry.target.classList.add('is-in');
      reveal.unobserve(entry.target);
    });
  }, { rootMargin: '0px 0px -8% 0px' });
  document.querySelectorAll('main .bx-sec > .bx-wrap, main .bx-sec > .bx-rail, main .bx-dark > .bx-wrap').forEach((element) => {
    if (element.getBoundingClientRect().top < window.innerHeight) return;
    element.classList.add('bx-reveal');
    reveal.observe(element);
  });
  // Safety net: content must never stay hidden if the observer does not fire.
  setTimeout(() => document.querySelectorAll('.bx-reveal').forEach((element) => element.classList.add('is-in')), 8000);
}

// Footer columns are always open on desktop, accordions on mobile.
const footerDesktop = window.matchMedia('(min-width: 860px)');
const syncFooter = () => {
  document.querySelectorAll('[data-bx-footer-col]').forEach((col) => {
    col.open = footerDesktop.matches;
    col.classList.toggle('is-open', footerDesktop.matches);
  });
};
syncFooter();
footerDesktop.addEventListener('change', syncFooter);

/* Tapday app banner is fixed at the top; let the sticky header sit below it while it shows. */
(() => {
  const root = document.documentElement;
  const sync = () => {
    const b = document.getElementById('fs-mab-banner');
    const on = b && b.classList.contains('fs-mab-visible') && getComputedStyle(b).display !== 'none';
    root.style.setProperty('--bx-appbar-h', on ? b.offsetHeight + 'px' : '0px');
  };
  const start = () => {
    const b = document.getElementById('fs-mab-banner');
    if (!b) return;
    new MutationObserver(sync).observe(b, { attributes: true, attributeFilter: ['class', 'style'] });
    new ResizeObserver(sync).observe(b);
    sync();
  };
  document.readyState === 'loading' ? document.addEventListener('DOMContentLoaded', start) : start();
})();
